import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Lock, ChevronRight, Mail } from "lucide-react";
import { toast } from "sonner";

export default function TrendPage() {
  const { user } = useAuth();
  const [trend, setTrend] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const trendRes = await api.get("/trends/today");
      setTrend(trendRes.data.trend);
      if (user?.subscribed) {
        const histRes = await api.get("/trends/history");
        setHistory(histRes.data.trends || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [user]);

  const subscribe = async () => {
    const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/emails/trend/send");
      if (data.skipped) toast.error("Envoi email désactivé");
      else toast.success(`Envoyé à ${user?.email}`);
    } catch {
      toast.error("Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl">
      <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FF453A] mb-3">
        Tendance du jour · Générée par Claude Sonnet 4.5
      </div>
      <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight mb-4" data-testid="trend-page-title">
        L'analyse IA quotidienne.
      </h1>
      <p className="text-white/50 mb-10">
        Chaque matin, l'IA analyse les dernières 24h de veille SEO mondiale et identifie LA tendance dominante.
      </p>

      {loading ? (
        <div className="text-white/40 font-mono text-sm">Chargement…</div>
      ) : !trend ? (
        <div className="text-white/40">Aucune tendance disponible pour le moment.</div>
      ) : (
        <>
          <article className="relative border border-white/10 bg-[#121215] p-8 md:p-12 mb-10" data-testid="trend-main-article">
            <div className="beam-border absolute inset-0 pointer-events-none" />
            <div className="relative z-10">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40 mb-4">
                {new Date(trend.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[1.05] mb-8">
                {trend.headline}
              </h2>
              {trend.locked ? (
                <div className="border border-[#FF453A]/40 bg-[#FF453A]/5 p-6 md:p-8">
                  <div className="flex items-center gap-2 text-[#FF453A] font-mono text-xs uppercase tracking-widest mb-3">
                    <Lock className="w-4 h-4" /> Contenu premium
                  </div>
                  <p className="text-white/70 mb-6">{trend.summary}</p>
                  <button
                    onClick={subscribe}
                    data-testid="trend-subscribe-btn"
                    className="bg-[#FF453A] text-black px-6 py-3 font-mono text-sm uppercase tracking-widest hover:bg-white transition-colors"
                  >
                    Se connecter · gratuit
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-10">
                    {trend.summary}
                  </p>
                  {trend.key_takeaways?.length > 0 && (
                    <div className="mb-10">
                      <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FFD60A] mb-4">
                        Les takeaways
                      </div>
                      <ul className="space-y-3">
                        {trend.key_takeaways.map((t, i) => (
                          <li key={i} className="flex items-start gap-3 text-white/85" data-testid={`trend-takeaway-${i}`}>
                            <span className="font-mono text-[#FF453A] text-sm shrink-0 mt-1">{String(i + 1).padStart(2, "0")}</span>
                            <span className="text-base leading-relaxed">{t}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {trend.sources?.length > 0 && (
                    <div className="pt-6 border-t border-white/10">
                      <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/40 mb-3">
                        Sources analysées
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {trend.sources.map((s, i) => (
                          <span key={i} className="text-xs font-mono px-3 py-1 border border-white/10 text-white/60">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {user?.subscribed && (
                    <button
                      onClick={sendEmail}
                      disabled={sending}
                      data-testid="trend-send-email-btn"
                      className="mt-8 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#FF8C42] hover:text-[#FAF0DE] transition-colors"
                    >
                      <Mail className="w-3.5 h-3.5" /> {sending ? "Envoi…" : "M'envoyer par email"}
                    </button>
                  )}
                </>
              )}
            </div>
          </article>

          {user?.subscribed && history.length > 1 && (
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/40 mb-4">
                Archives
              </div>
              <div className="border-t border-l border-white/10">
                {history.filter(h => h.date !== trend.date).map((h) => (
                  <div key={h.trend_id} className="p-5 border-r border-b border-white/10 flex items-center gap-6" data-testid={`trend-archive-${h.trend_id}`}>
                    <div className="text-xs font-mono text-white/40 w-24 shrink-0">{h.date}</div>
                    <div className="flex-1 font-display text-lg font-semibold">{h.headline}</div>
                    <ChevronRight className="w-4 h-4 text-white/30" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
